/**
 * Free PDF markup tools comparison data.
 * Each cell includes the stated value, the source URL where it was verified, and the checked date.
 * Facts that cannot be verified from a fetched page are marked '--'.
 */

export interface ComparisonCell {
  value: string;
  sourceUrl?: string;
  checked?: string; // YYYY-MM-DD format
}

export interface ToolComparison {
  name: string;
  url: string;
  accountRequired: ComparisonCell;
  uploadOrLocal: ComparisonCell;
  freeExportWatermark: ComparisonCell;
  platforms: ComparisonCell;
  offlineSupport: ComparisonCell;
}

/** iPad Safari evicts offline data after 7 days without a visit unless the site is on the Home Screen (docs/offline.md). */
export const IPAD_OFFLINE_NOTE = 'On iPad, add it to your Home Screen — Safari clears offline data after 7 days without a visit.';

export const TOOLS_COMPARISON: ToolComparison[] = [
  {
    name: 'PDF Markup Tool',
    url: 'https://pdfmarkuptool.com/',
    accountRequired: {
      value: 'No',
      sourceUrl: 'https://pdfmarkuptool.com/',
      checked: '2026-10-18',
    },
    uploadOrLocal: {
      value: 'Local processing in browser',
      sourceUrl: 'https://pdfmarkuptool.com/',
      checked: '2026-10-18',
    },
    freeExportWatermark: {
      value: 'No watermark',
      sourceUrl: 'https://pdfmarkuptool.com/',
      checked: '2026-10-18',
    },
    platforms: {
      value: 'Browser (Mac, Windows, Linux); iPad support',
      sourceUrl: 'https://pdfmarkuptool.com/',
      checked: '2026-10-18',
    },
    offlineSupport: {
      // the service worker (docs/offline.md): e2e/offline.spec.ts proves it; offlineClaims.test.ts pins the words
      value: 'Yes (works offline after your first visit)',
      sourceUrl: 'https://pdfmarkuptool.com/',
      checked: '2026-11-02',
    },
  },
  {
    name: 'Redline PDF',
    url: 'https://redlinepdf.net',
    accountRequired: {
      value: 'Yes (demo available without account)',
      sourceUrl: 'https://redlinepdf.net',
      checked: '2026-10-18',
    },
    uploadOrLocal: {
      value: 'Browser processing + server storage (optional)',
      sourceUrl: 'https://redlinepdf.net',
      checked: '2026-10-18',
    },
    freeExportWatermark: {
      value: 'Redline footer on free exports',
      sourceUrl: 'https://redlinepdf.net/pricing',
      checked: '2026-10-18',
    },
    platforms: {
      value: 'Browser (Chrome, Edge, Safari, Firefox on Windows, macOS, Linux, iPad, Android)',
      sourceUrl: 'https://redlinepdf.net',
      checked: '2026-10-18',
    },
    offlineSupport: {
      value: '--',
      sourceUrl: undefined,
      checked: undefined,
    },
  },
  {
    name: 'ContractorCounter',
    url: 'https://contractorcounter.com',
    accountRequired: {
      value: 'Free account available (no card required)',
      sourceUrl: 'https://contractorcounter.com',
      checked: '2026-10-18',
    },
    uploadOrLocal: {
      value: '--',
      sourceUrl: undefined,
      checked: undefined,
    },
    freeExportWatermark: {
      value: 'Not included on free plan',
      sourceUrl: 'https://contractorcounter.com/pricing',
      checked: '2026-10-18',
    },
    platforms: {
      value: 'Browser (Mac, Windows, iPad, iPhone, Android)',
      sourceUrl: 'https://contractorcounter.com',
      checked: '2026-10-18',
    },
    offlineSupport: {
      value: '--',
      sourceUrl: undefined,
      checked: undefined,
    },
  },
  {
    name: 'SolidTakeoff',
    url: 'https://solidtakeoff.com',
    accountRequired: {
      value: 'No (account optional for cloud features)',
      sourceUrl: 'https://solidtakeoff.com',
      checked: '2026-10-18',
    },
    uploadOrLocal: {
      value: 'Local browser processing (with cloud option)',
      sourceUrl: 'https://solidtakeoff.com',
      checked: '2026-10-18',
    },
    freeExportWatermark: {
      value: '"Generated with SolidTakeoff.com" footer on free exports',
      sourceUrl: 'https://solidtakeoff.com/pricing',
      checked: '2026-10-18',
    },
    platforms: {
      value: 'Browser-based (Mac, Windows, iPad and others)',
      sourceUrl: 'https://solidtakeoff.com',
      checked: '2026-10-18',
    },
    offlineSupport: {
      value: '--',
      sourceUrl: undefined,
      checked: undefined,
    },
  },
  {
    name: 'KresCAD',
    url: 'https://krescad.com',
    accountRequired: {
      value: 'No (no card required)',
      sourceUrl: 'https://krescad.com',
      checked: '2026-10-18',
    },
    uploadOrLocal: {
      value: 'Local browser processing',
      sourceUrl: 'https://krescad.com/faq',
      checked: '2026-10-18',
    },
    freeExportWatermark: {
      value: 'Watermark-free',
      sourceUrl: 'https://krescad.com/faq',
      checked: '2026-10-18',
    },
    platforms: {
      value: 'Desktop and laptop browsers (mobile/iPad support planned)',
      sourceUrl: 'https://krescad.com',
      checked: '2026-10-18',
    },
    offlineSupport: {
      value: '--',
      sourceUrl: undefined,
      checked: undefined,
    },
  },
];
